/**
 * UTM Parameter Tracking Utility with Enhanced Attribution Logic
 * Captures, stores, and retrieves UTM parameters from URL and storage
 * Infers source/medium from referrer, click IDs, and URL parameters
 */

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid'
];

const STORAGE_KEY = 'utm_tracking_data';
const SESSION_STORAGE_KEY = 'utm_session_timestamp';

// Known search engines for attribution
const SEARCH_ENGINES = {
  'google': 'google',
  'bing': 'bing',
  'yahoo': 'yahoo',
  'duckduckgo': 'duckduckgo',
  'ecosia': 'ecosia',
  'baidu': 'baidu',
  'yandex': 'yandex',
  'ask': 'ask'
};

// Known social networks for attribution
const SOCIAL_NETWORKS = {
  'facebook': 'facebook',
  'instagram': 'instagram',
  'linkedin': 'linkedin',
  'twitter': 'twitter',
  'x.com': 'x', // Rebranded Twitter
  'tiktok': 'tiktok',
  'youtube': 'youtube',
  'reddit': 'reddit',
  'pinterest': 'pinterest',
  'snapchat': 'snapchat',
  'telegram': 'telegram',
  'whatsapp': 'whatsapp'
};

/**
 * Parse URL parameters from a query string
 * @param {string} queryString - The query string (without ?)
 * @returns {object} Parsed parameters
 */
const parseQueryParams = (queryString) => {
  if (!queryString) return {};

  const params = new URLSearchParams(queryString);
  const result = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
};

/**
 * Extract domain name from a URL
 * @param {string} urlString - Full URL or domain
 * @returns {string} Domain name (e.g., "google" from "https://www.google.com/search")
 */
const extractDomain = (urlString) => {
  if (!urlString) return '';

  try {
    // Handle both full URLs and plain domains
    const urlObj = urlString.startsWith('http') ? new URL(urlString) : new URL(`https://${urlString}`);
    const hostname = urlObj.hostname;

    // Remove www. and get the main domain
    return hostname.replace('www.', '').split('.')[0].toLowerCase();
  } catch (error) {
    // If URL parsing fails, try simple string extraction
    const parts = urlString.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0].split('.');
    return parts.length > 0 ? parts[0].toLowerCase() : '';
  }
};

/**
 * Check if a referrer domain is the same as the current domain
 * @param {string} referrer - Referrer URL
 * @param {string} currentDomain - Current domain
 * @returns {boolean} True if referrer is from current domain
 */
const isInternalReferrer = (referrer, currentDomain) => {
  if (!referrer || referrer === 'direct') return false;

  try {
    const referrerDomain = new URL(referrer).hostname;
    // Compare with current domain or any www variant
    return referrerDomain === currentDomain ||
           referrerDomain === `www.${currentDomain}` ||
           currentDomain === referrerDomain.replace('www.', '');
  } catch (error) {
    return false;
  }
};

/**
 * Get current domain
 * @returns {string} Current domain hostname
 */
const getCurrentDomain = () => {
  if (typeof window === 'undefined') return '';
  try {
    return window.location.hostname;
  } catch (error) {
    return '';
  }
};

/**
 * Infer utm_source and utm_medium from referrer URL
 * @param {string} referrer - Referrer URL
 * @returns {object} Object with utm_source and utm_medium
 */
const inferAttributionFromReferrer = (referrer) => {
  if (!referrer || referrer === 'direct' || !referrer.startsWith('http')) {
    return { utm_source: '', utm_medium: '' };
  }

  const domain = extractDomain(referrer);

  // Check if it's a known search engine
  if (SEARCH_ENGINES[domain]) {
    return {
      utm_source: SEARCH_ENGINES[domain],
      utm_medium: 'organic'
    };
  }

  // Check if it's a known social network
  if (SOCIAL_NETWORKS[domain]) {
    return {
      utm_source: SOCIAL_NETWORKS[domain],
      utm_medium: 'social'
    };
  }

  // Any other external domain is referral traffic
  if (domain) {
    return {
      utm_source: domain,
      utm_medium: 'referral'
    };
  }

  return { utm_source: '', utm_medium: '' };
};

/**
 * Get current page URL and referrer
 * @returns {object} Object with landingPageUrl and referrer
 */
const getPageInfo = () => {
  if (typeof window === 'undefined') {
    return {
      landing_page_url: '',
      referrer: ''
    };
  }

  return {
    landing_page_url: window.location.href,
    referrer: document.referrer || 'direct'
  };
};

/**
 * Extract UTM parameters from URL
 * @returns {object} Object containing UTM parameters and page info
 */
const extractUTMFromURL = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const queryString = window.location.search.slice(1);
  const allParams = parseQueryParams(queryString);
  const utmData = {};

  // Extract UTM and click ID parameters
  UTM_PARAMS.forEach(param => {
    if (allParams[param]) {
      utmData[param] = allParams[param];
    }
  });

  return utmData;
};

/**
 * Get UTM data from localStorage
 * @returns {object} Stored UTM data or empty object
 */
const getStoredUTMData = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.warn('Failed to retrieve UTM data from localStorage:', error);
    return {};
  }
};

/**
 * Save UTM data to localStorage
 * @param {object} utmData - UTM parameters to store
 */
const saveUTMData = (utmData) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(utmData));
    // Store timestamp to track session
    sessionStorage.setItem(SESSION_STORAGE_KEY, new Date().getTime().toString());
  } catch (error) {
    console.warn('Failed to save UTM data to localStorage:', error);
  }
};

/**
 * Clear UTM data from localStorage
 */
const clearUTMData = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear UTM data:', error);
  }
};

/**
 * Get default UTM values when no parameters are present
 * @returns {object} Default UTM object
 */
const getDefaultUTMValues = () => {
  return {
    utm_source: 'direct',
    utm_medium: 'none',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    gclid: '',
    fbclid: ''
  };
};

/**
 * Apply attribution logic based on priority rules
 * Priority:
 * 1. UTM parameters in URL
 * 2. gclid (Google Ads)
 * 3. fbclid (Facebook Ads)
 * 4. Search engine referrer
 * 5. Social network referrer
 * 6. External referrer
 * 7. Direct traffic
 *
 * @param {object} urlParams - URL parameters
 * @param {string} referrer - HTTP referrer
 * @returns {object} Attribution data with utm_source and utm_medium
 */
const applyAttributionLogic = (urlParams, referrer) => {
  const currentDomain = getCurrentDomain();

  // Rule 1: Check for explicit UTM parameters
  if (urlParams.utm_source && urlParams.utm_medium) {
    return {
      utm_source: urlParams.utm_source,
      utm_medium: urlParams.utm_medium,
      attribution_type: 'utm_explicit'
    };
  }

  // Rule 2: Check for gclid (Google Ads)
  if (urlParams.gclid) {
    return {
      utm_source: 'google',
      utm_medium: 'cpc',
      attribution_type: 'gclid'
    };
  }

  // Rule 3: Check for fbclid (Facebook Ads)
  if (urlParams.fbclid) {
    return {
      utm_source: 'facebook',
      utm_medium: 'paid_social',
      attribution_type: 'fbclid'
    };
  }

  // Rule 4-6: Infer from referrer (if not internal)
  if (referrer && referrer !== 'direct' && !isInternalReferrer(referrer, currentDomain)) {
    const inferred = inferAttributionFromReferrer(referrer);
    if (inferred.utm_source) {
      return {
        utm_source: inferred.utm_source,
        utm_medium: inferred.utm_medium,
        attribution_type: 'referrer'
      };
    }
  }

  // Rule 7: Default to direct traffic
  return {
    utm_source: 'direct',
    utm_medium: 'none',
    attribution_type: 'direct'
  };
};

/**
 * Initialize and capture UTM parameters with enhanced attribution
 * Applies attribution logic:
 * 1. Explicit UTM parameters take priority
 * 2. Click IDs (gclid, fbclid) infer source/medium
 * 3. Referrer is analyzed for search engines, social, or domains
 * 4. Defaults to direct traffic
 *
 * @returns {object} Captured UTM data with resolved source/medium
 */
const initializeUTMTracking = () => {
  if (typeof window === 'undefined') {
    return getDefaultUTMValues();
  }

  // First, check for stored UTM data (first-touch attribution)
  const storedData = getStoredUTMData();
  if (Object.keys(storedData).length > 0) {
    // Return stored data - this is first-touch attribution
    return storedData;
  }

  // No stored data, so let's capture new attribution
  const pageInfo = getPageInfo();
  const allURLParams = parseQueryParams(window.location.search.slice(1));

  // Apply attribution logic to determine source/medium
  const attribution = applyAttributionLogic(allURLParams, pageInfo.referrer);

  // Build complete data object
  const dataToStore = {
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: allURLParams.utm_campaign || '',
    utm_term: allURLParams.utm_term || '',
    utm_content: allURLParams.utm_content || '',
    gclid: allURLParams.gclid || '',
    fbclid: allURLParams.fbclid || '',
    landing_page_url: pageInfo.landing_page_url,
    referrer: pageInfo.referrer,
    attribution_type: attribution.attribution_type,
    captured_at: new Date().toISOString()
  };

  // Store this as first-touch attribution
  saveUTMData(dataToStore);

  return dataToStore;
};

/**
 * Get UTM data for form submission
 * Returns complete UTM object with all fields and applied attribution logic
 * This data includes first-touch attribution captured on initial page load
 *
 * @returns {object} Complete UTM data for form submission
 */
const getUTMDataForForm = () => {
  const utmData = initializeUTMTracking();

  // Ensure all fields are present with proper defaults
  return {
    utm_source: utmData.utm_source || 'direct',
    utm_medium: utmData.utm_medium || 'none',
    utm_campaign: utmData.utm_campaign || '',
    utm_term: utmData.utm_term || '',
    utm_content: utmData.utm_content || '',
    gclid: utmData.gclid || '',
    fbclid: utmData.fbclid || '',
    landing_page_url: utmData.landing_page_url || '',
    referrer: utmData.referrer || 'direct',
    utm_captured_at: utmData.captured_at || new Date().toISOString(),
    attribution_type: utmData.attribution_type || 'unknown'
  };
};

export {
  initializeUTMTracking,
  getUTMDataForForm,
  extractUTMFromURL,
  getStoredUTMData,
  saveUTMData,
  clearUTMData,
  applyAttributionLogic,
  inferAttributionFromReferrer,
  extractDomain,
  isInternalReferrer,
  getCurrentDomain,
  UTM_PARAMS,
  STORAGE_KEY,
  SEARCH_ENGINES,
  SOCIAL_NETWORKS
};
